import type { ApolloClient } from "@apollo/client";
import { COMMITTEE_ASSIGNMENTS_PAGE_SIZE, COMMITTEE_PARTICIPANT_EMAIL_MAX_PAGES } from "../constants";
import { BACKOFFICE_COMMITTEE_PARTICIPANT_EMAILS_QUERY } from "../queries";
import {
  DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY,
  type BackofficeCommitteeParticipantEmailsQueryResponse,
  type BackofficeCommitteeParticipantEmailsQueryVariables,
  type CommitteeParticipantEmailNode
} from "../types";

export const MAILTO_MAX_LENGTH = 2000;

export type MailtoField = "to" | "cc" | "bcc";

export type CommitteeParticipantEmailResult = {
  emails: string[];
  participantCount: number;
  excludedCount: number;
  truncated: boolean;
};

export function isUnusableEmail(email: string | null | undefined): boolean {
  const trimmed = email?.trim() ?? "";
  if (!trimmed) return true;
  return trimmed.toLowerCase().endsWith(".invalid");
}

export function collectUsableEmails(emails: Array<string | null | undefined>): string[] {
  const seen = new Set<string>();
  const result: string[] = [];

  for (const raw of emails) {
    if (isUnusableEmail(raw)) continue;
    const trimmed = raw?.trim() ?? "";
    const key = trimmed.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(trimmed);
  }

  return result;
}

export function formatEmailAddressList(emails: string[]): string {
  return emails.join(",");
}

export function buildCommitteeMailtoHref({
  emails,
  field,
  subject
}: {
  emails: string[];
  field: MailtoField;
  subject: string;
}): string {
  const encodedSubject = encodeURIComponent(subject);
  const list = formatEmailAddressList(emails);
  const encodedList = emails.map((email) => encodeURIComponent(email)).join(",");

  if (field === "to") {
    return `mailto:${list}?subject=${encodedSubject}`;
  }

  if (field === "cc") {
    return `mailto:?cc=${encodedList}&subject=${encodedSubject}`;
  }

  return `mailto:?bcc=${encodedList}&subject=${encodedSubject}`;
}

export function isMailtoTooLong(href: string): boolean {
  return href.length > MAILTO_MAX_LENGTH;
}

export function openMailtoHref(href: string): void {
  window.location.assign(href);
}

export function collectEmailsFromAssignmentNodes(
  nodes: Array<CommitteeParticipantEmailNode | null | undefined>
): CommitteeParticipantEmailResult {
  const seenPeople = new Set<string>();
  const rawEmails: string[] = [];
  let participantCount = 0;
  let excludedCount = 0;

  for (const node of nodes) {
    const person = node?.person;
    if (!person?.id) continue;
    if (seenPeople.has(person.id)) continue;
    seenPeople.add(person.id);
    participantCount += 1;

    if (isUnusableEmail(person.email)) {
      excludedCount += 1;
      continue;
    }

    rawEmails.push(person.email?.trim() ?? "");
  }

  return {
    emails: collectUsableEmails(rawEmails),
    participantCount,
    excludedCount,
    truncated: false
  };
}

export async function fetchCommitteeParticipantEmails(
  client: ApolloClient<object>,
  { committeeId, includeInactive }: { committeeId: string; includeInactive: boolean }
): Promise<CommitteeParticipantEmailResult> {
  const nodes: CommitteeParticipantEmailNode[] = [];
  let after: string | undefined;
  let hasNextPage = true;
  let truncated = false;
  let pages = 0;

  while (hasNextPage) {
    if (pages >= COMMITTEE_PARTICIPANT_EMAIL_MAX_PAGES) {
      truncated = true;
      break;
    }

    const variables: BackofficeCommitteeParticipantEmailsQueryVariables = {
      id: committeeId,
      first: COMMITTEE_ASSIGNMENTS_PAGE_SIZE,
      after,
      orderBy: DEFAULT_COMMITTEE_ASSIGNMENT_ORDER_BY
    };
    if (!includeInactive) {
      variables.status = "ACTIVE";
    }

    const { data } = await client.query<
      BackofficeCommitteeParticipantEmailsQueryResponse,
      BackofficeCommitteeParticipantEmailsQueryVariables
    >({
      query: BACKOFFICE_COMMITTEE_PARTICIPANT_EMAILS_QUERY,
      variables,
      fetchPolicy: "no-cache"
    });

    const connection = data.backofficeCommittee?.assignments;
    nodes.push(...(connection?.nodes ?? []));
    pages += 1;

    hasNextPage = Boolean(connection?.pageInfo.hasNextPage);
    const nextAfter = connection?.pageInfo.endCursor ?? undefined;
    if (hasNextPage && (!nextAfter || nextAfter === after)) {
      truncated = true;
      break;
    }
    after = nextAfter;
  }

  return { ...collectEmailsFromAssignmentNodes(nodes), truncated };
}
